import { useEffect, useState, useCallback, useRef } from 'react';
import { ErrorMessage, Field, Form, FormikHelpers, FormikProvider, useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../core/store/store";
import { ContainerModal } from "./ContainerModal";
import { CustomButton } from "../../../../core/components/CustomButton";
import { CustomInput } from "../../../../core/components/CustomInput";
import { Row } from "../../../../core/components/Row";
import { onGetMatchesPartner } from '../../../match/presentation/store/MatchThunk';
import { SearchMatchesParams } from '../../../match/domain/entities/getSearchMatchesParams';
import useDebounce from "../../../../core/hooks/useDebounce";
import { addPartnerValidationSchema, AddPartnerFormValues } from '../interface/AddpartnerFormInterface';
import { TextComponent } from '../../../../core/components/TextComponent';
import { GetMyMatchesParams } from '../../../match/domain/usecases/getMyMatches';
import { v4 as uuidv4 } from "uuid";
import { onSendInvitation } from '../store/PartnerThunk';
import { toast } from 'react-toastify';
import { Likes } from '../../../match/domain/entities/userMatch';
import { deleteMyMatches } from "../../../match/presentation/store/MatchSlice";
import { NavigationButton } from "../../../match/presentation/components/NavigationButton";

interface AddPartnerPropsComponent {
    isOpen: boolean;
    onRequestClose: () => void;
}

export const AddPartnerComponent: React.FC<AddPartnerPropsComponent> = ({ isOpen, onRequestClose }) => {
    const dispatch: AppDispatch = useDispatch();
    const { myMatches: searchMatches = [], matchesPartnerLoading } = useSelector((state: RootState) => state.match);
    const { profileConfig } = useSelector((state: RootState) => state.onboarding);
	const { partners } = useSelector((state: RootState) => state.partner);
    const [isButtonLoading, setIsButtonLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isListVisible, setIsListVisible] = useState(false);
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const [selectedMatch, setSelectedMatch] = useState<Likes | null>(null);
    const [isSearchModalOpen, setIsSearchModalOpen] = useState(isOpen);
    const notificationRef = useRef<HTMLDivElement>(null); 
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
   
    const handleConfirm = async (
        values: AddPartnerFormValues,
        { setSubmitting, resetForm }: FormikHelpers<AddPartnerFormValues>
    ) => {
        setIsButtonLoading(true);
        setErrorMessage(null);
        setSubmitting(true);
        
        const partnerRequestData   = {
            id: uuidv4(),
            user_id: selectedMatch?.user_id ?? "",
            match_id: selectedMatch?.match_id ?? ""
        };

        try {
            setIsButtonLoading(false);
            dispatch(onSendInvitation(partnerRequestData));
            dispatch(deleteMyMatches());
            resetForm();
            onRequestClose(); 

        } catch (error) {
            const errorMsg = error instanceof Error ? error.message : "Error desconocido";
            console.error('Error al enviar la solicitud:', errorMsg);
            setErrorMessage(errorMsg); 
            toast.error(errorMsg); 
        } finally {
            setIsButtonLoading(false);
            setSubmitting(false); 
            setIsImageModalOpen(false);
        }
    };
    

    const formik = useFormik<AddPartnerFormValues>({
        initialValues: {
            username: "",
        },
        validationSchema: addPartnerValidationSchema,
        validateOnChange: false,
        validateOnBlur: false,
        onSubmit: handleConfirm
    });
    
    const debounceSearchValue = useDebounce(formik.values.username, 1000);
    
    useEffect(() => {
        if (isSearchModalOpen && debounceSearchValue.trim() !== '' && formik.values.username !== "") {
            const userLat = '0';
            const userLng = '0';
            
            const params: SearchMatchesParams = {
                user_lat: userLat,
                user_lng: userLng,
                page: "1",
                limit: "10",
                search: debounceSearchValue.trim(),
            }; 
            
            dispatch(onGetMatchesPartner(new GetMyMatchesParams(params)))
            .catch(error => {
                console.error('Error fetching matches:', error);
                setErrorMessage("Error fetching matches. Please try again.");
            });
        }
    }, [isSearchModalOpen, debounceSearchValue, dispatch]);
    
    useEffect(() => {
        setIsSearchModalOpen(isOpen);
    }, [isOpen]);

    const handleSelect = useCallback((match: Likes) => {
        setSelectedMatch(match);
        formik.setFieldValue("username", match.username);
        setIsListVisible(false);
    }, [dispatch, formik, profileConfig]);

    const handleClose = () => {
        setIsSearchModalOpen(false);
        formik.resetForm();
        formik.setFieldValue("username", "");
        setSelectedMatch(null);
        dispatch(deleteMyMatches());
        onRequestClose();
    };
    
    const handleConfirmNo = () => {
        setIsImageModalOpen(false);
        formik.setFieldValue("username", "");
        dispatch(deleteMyMatches());
        setSelectedMatch(null);
        setIsListVisible(false);
        setIsSearchModalOpen(true);
    };
    
    const handleConfirmOpen = () => {
        if (selectedMatch) {
            setCurrentImageIndex(0);
            setIsImageModalOpen(true);
            setIsSearchModalOpen(false);
        }
    };
    
    const handleNextImage = () => {
        if (selectedMatch && currentImageIndex < selectedMatch.profile_pictures.length - 1) {
            setCurrentImageIndex(currentImageIndex + 1);
        } else {
            setCurrentImageIndex(-1);
        }
    };
    
    const handlePreviousImage = () => {
        if (selectedMatch && currentImageIndex > 0) {
            setCurrentImageIndex(currentImageIndex - 1);
        } else {
            setCurrentImageIndex(selectedMatch ? selectedMatch.profile_pictures.length - 1 : 0);
        }
    };
    
    const orderedProfilePictures = selectedMatch ? [...selectedMatch.profile_pictures] : [];

    const getFilteredMatches = () => {
        return searchMatches.filter((m) => 
            (!m.partner || m.partner.status !== "accepted") && 
            !partners.some((p) => p.user_id === m.user_id) &&
            m.status !== "banned" && m.status !== "inactive" &&
            m.status_user_id1 !== "blocked" && m.status_user_id2 !== "blocked"
        );
    };

    return (
        <>
            <FormikProvider value={formik}>
                <ContainerModal
                    isOpen={isSearchModalOpen}
                    onRequestClose={handleClose}
                    title="Add Match"
                    subtitle="Please select your match."
                    >
                    <div className="flex flex-col h-full">
                        {errorMessage && <div className="text-red-500">{errorMessage}</div>}
                        <Form className="flex flex-col flex-grow">
                            <div className="flex-grow relative">
                            <Field
                              as={CustomInput}
                              name="username"
                              hintText="Search"
                              label="Match"
                              type="text"
                              className="form-control"
                              autoComplete={formik.values ? "off" : undefined}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const value = e.target.value;
                                formik.setFieldValue("username", value);
                                setIsListVisible(value.trim() !== "");
                                if (value.trim() === "") {
                                  setIsListVisible(false);
                                } else {
                                  setIsListVisible(true);
                                }
                              }}
                              onBlur={formik.handleBlur}
                              value={formik.values.username}
                              required
                              isLoading={matchesPartnerLoading && debounceSearchValue === formik.values.username}
                              disabled={matchesPartnerLoading}
                            />
                                <ErrorMessage name="username" component="div" className="text-red-500" />
                                {!matchesPartnerLoading && isListVisible && formik.values.username.trim() !== "" && (() => {
                                    const filteredMatches = getFilteredMatches();
                                    return filteredMatches.length > 0 ? (
                                        <div className="absolute z-50 w-full mt-0 bg-white border border-gray-300 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                                            <ul className="list-none p-0">
                                                {filteredMatches.map((m) => (
                                                    <li
                                                        key={m.user_id}
                                                        className={`flex items-center py-1 px-2 ${m.user_id === selectedMatch?.user_id ? 'bg-gray-200' : 'hover:bg-gray-100'} cursor-pointer`}
                                                        onClick={(e: React.MouseEvent<HTMLLIElement>) => {
                                                            e.stopPropagation();
                                                            handleSelect(m);
                                                        }} 
                                                    >
                                                        <Row className="py-3 gap-2" crossAxisAlignment="center">
                                                            <img
                                                                className="w-10 aspect-square object-cover rounded-full cursor-pointer hover:opacity-90"
                                                                src={m.profile_pictures[0].url ?? ""}
                                                                draggable={false}
                                                                alt={`profile-${m.username}`}
                                                            />
                                                            <TextComponent className="truncate" variant="h5">
                                                                {m.username}
                                                            </TextComponent>
                                                        </Row>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    ) : (
                                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                                            <div className="text-center py-2">No matches found</div>
                                        </div>
                                    );
                                })()}
                            </div>
                            <CustomButton
                                type="button"
                                className="mt-auto mb-4 sm:mt-10 sm:mb-24"
                                loading={isButtonLoading}
                                disabled={isButtonLoading || (!formik.values.username.trim() && !selectedMatch?.user_id)}
                                onClick={handleConfirmOpen}
                            >
                                Confirm
                            </CustomButton>
                        </Form>
                    </div>
                </ContainerModal>
            </FormikProvider>

            {isImageModalOpen && selectedMatch && (
                <>
                    <div className="fixed inset-0 bg-[#0000006b] z-50" />
                    <div className="fixed z-50 max-md:w-full h-3/5 md:h-4/5 flex items-center justify-center top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                        {currentImageIndex > 0 && (
                            <NavigationButton
                                onClick={handlePreviousImage}
                                className="left-0 md:left-[-50px] text-white"
                                ariaLabel="Previous Image"
                                isReversed={false}
                                isBackground={false}
                            />
                        )}
                        {selectedMatch.profile_pictures && currentImageIndex < selectedMatch.profile_pictures.length - 1 && (
                            <NavigationButton
                                onClick={handleNextImage}
                                className="right-0 md:right-[-50px] text-white"
                                ariaLabel="Next Image"
                                isReversed={true}
                                isBackground={false}
                            />
                        )}
                       <aside
                            ref={notificationRef}
                            className={`bg-black transition-transform duration-300 ease-out shadow-lg w-4/5 md:w-[30rem] h-full`}
                            style={{
                                borderRadius: '10px',
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="w-full h-full relative ">
                                <div className="absolute inset-0 bg-black/40 rounded-lg" />
                                <div className="absolute top-10 left-1/2 transform -translate-x-1/2 flex space-x-2">
                                    {selectedMatch.profile_pictures.map((_, index) => (
                                        <svg
                                            key={index}
                                            width={index === currentImageIndex ? "6" : "7"}
                                            height="6"
                                            viewBox="0 0 7 6"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                            onClick={() => setCurrentImageIndex(index)}
                                            className="cursor-pointer"
                                        >
                                            <circle
                                                cx={index === currentImageIndex ? "3" : "3.5"}
                                                cy="3"
                                                r={index === currentImageIndex ? "3" : "2.5"}
                                                fill={index === currentImageIndex ? "white" : "none"}
                                                stroke={index === currentImageIndex ? "none" : "white"}
                                                strokeOpacity={index === currentImageIndex ? "0" : "0.46"}
                                            />
                                        </svg>
                                    ))}
                                </div>
                                
                                <img 
                                    src={orderedProfilePictures[currentImageIndex].url} 
                                    alt={selectedMatch.username} 
                                    className="w-full h-full object-cover rounded-lg" 
                                />
                                <TextComponent variant='h5' className='absolute top-14 px-10 left-1/2 transform -translate-x-1/2 text-lg text-white text-center w-full font-bold'>
                                    Would you like to add {selectedMatch.username} as your partner?
                                </TextComponent>
                                
                                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex justify-between w-full gap-8 px-8">
                                    <CustomButton 
                                        type="button" 
                                        onClick={() => { handleConfirm(formik.values, formik);}}
                                        className="w-1/2"
                                        small
                                    >
                                        Yes
                                    </CustomButton>
                                    <CustomButton 
                                        type="button"
                                        onClick={handleConfirmNo} 
                                        className="w-1/2"
                                        small
                                    >
                                        No
                                    </CustomButton>
                                </div>
                            </div>
                        </aside>
                    </div>
                </>
            )} 
        </>
    );
};
